<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("page.schedule.generating_fights") }}
        </template>
        <template v-slot:body>
            <div class="columns is-centered is-mobile">
                <div class="column is-12" v-if="initDone">
                    <div v-if="hasPartiallyScheduledBrackets">
                        <div class="has-text-danger">
                            <i class="fa fa-ban" />
                            {{ translate("page.schedule.partially_scheduled_brackets") }}
                        </div>
                        <div class="content">
                            <ul>
                                <li v-for="bracket in partiallyScheduledBrackets" :key="bracket.parameterId">
                                    {{ bracket.name }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div v-else-if="stateUpToDate && anythingToGenerate">
                        <span class="has-text-success">
                            <i class="fa fa-check" />
                            {{ translate("page.schedule.brackets_up_to_date") }}
                        </span>
                    </div>
                    <div v-else-if="stateUpToDate">
                        <span class="has-text-warning">
                            {{ translate("page.schedule.no_brackets_to_generate_fights") }}
                        </span>
                    </div>
                    <div v-else>
                        <div class="has-text-danger">
                            {{ translate("page.schedule.invalid_brackets_state") }}
                        </div>
                        <div class="content">
                            <ul>
                                <li v-for="bracket in outdatedBrackets" :key="bracket.parameter_id">
                                    {{ bracket.name }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <template v-else>
                    <LoadingSpinner2 class="has-added-margin" />
                </template>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>

                <LoadingButton
                    :is-loading="processingFightsInProgress"
                    @click="confirm"
                    class="button"
                    :class="{ 'is-success': stateUpToDate && initDone, 'is-danger': !stateUpToDate && initDone }"
                    :disabled="!anythingToGenerate || hasPartiallyScheduledBrackets"
                >
                    <template v-if="stateUpToDate">
                        <span class="icon"><i class="fa fa-check" /></span>
                        <span> {{ translate("common.continue") }} </span>
                    </template>
                    <template v-else>
                        <span class="icon">
                            <i class="fa fa-exclamation-triangle" />
                        </span>
                        <span>{{ translate("common.continue_anyway") }}</span>
                    </template>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import { translate } from "../Common/translations";
    import { successToast } from "../Common/toasts";
    import { handleError } from "../Common/errorHandler";
    import { useModal } from "../Common/useModal";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import fightsApi from "../Api/fights";
    import { ComplexBracket } from "../Common/Brackets/types";
    import { PartiallyScheduledBracket } from "../Api/schedules";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, close: closeModal } = useModal();

    const processingFightsInProgress = ref(false);
    const initDone = ref(false);
    const totalBrackets = ref(0);
    const outdatedBrackets = ref<ComplexBracket[]>([]);
    const partiallyScheduledBrackets = ref<PartiallyScheduledBracket[]>([]);

    const stateUpToDate = computed(() => outdatedBrackets.value.length === 0);
    const anythingToGenerate = computed(() => totalBrackets.value > 0);
    const hasPartiallyScheduledBrackets = computed(() => partiallyScheduledBrackets.value.length > 0);

    function fetchData() {
        return fightsApi
            .getFightsGenerationStatus(props.eventId)
            .then(resp => {
                const complexBrackets = resp.complexBrackets;

                totalBrackets.value = complexBrackets.length;
                outdatedBrackets.value = complexBrackets.filter(complexBracket => {
                    return complexBracket.isOutdated;
                });
                partiallyScheduledBrackets.value = resp.partiallyScheduledBrackets ?? [];
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function confirm() {
        processingFightsInProgress.value = true;

        return fightsApi
            .generateAllFights(props.eventId)
            .then(() => {
                close();
                successToast(translate("flash_messages.schedules_were_saved"));
            })
            .catch(handleError)
            .finally(() => {
                processingFightsInProgress.value = false;
            });
    }

    function open() {
        processingFightsInProgress.value = false;
        initDone.value = false;
        totalBrackets.value = 0;
        outdatedBrackets.value = [];
        partiallyScheduledBrackets.value = [];
        modal.value?.open();
        fetchData();
    }

    function close() {
        closeModal();
    }

    defineExpose({ open });
</script>
